import { HttpContext } from "@angular/common/http";
import { inject } from "@angular/core";
import { ActivatedRouteSnapshot, ResolveFn } from "@angular/router";
import { combineLatest, map, Observable, switchMap } from "rxjs";
import { ConfigService } from "../config/config.service";
import { mergeDocumentLanguages } from "../helpers/document-languages.helper";
import { pathParamsHelper } from "../helpers/path-params.helper";
import { DefaultDocCat } from "../pia-api/generated";
import { PiaDocumentsService } from "../pia-api/pia-documents.service";
import { PiaProductsService } from "../pia-api/pia-products.service";
import { DocumentLanguagesStorageService } from "../services/document-languages-storage.service";
import { WebStorage } from "../services/web-storage";
import { IS_IN_RESOLVER_CONTEXT } from "../tokens/resolver-context.token";
import { withLoading } from "../utils/with-loading";

export const productDocumentCategoriesResolver: ResolveFn<
  Observable<DefaultDocCat[] | null>
> = withLoading(
  (route: ActivatedRouteSnapshot): Observable<DefaultDocCat[] | null> => {
    const piaDocumentsService = inject(PiaDocumentsService);
    const piaProductsService = inject(PiaProductsService);
    const documentLanguagesStorageService = inject(
      DocumentLanguagesStorageService,
    );
    const config = inject(ConfigService);
    const webStorage = inject(WebStorage);
    const { productID, countryID, languageID, year } = pathParamsHelper(route);
    const defaultLanguage = config.languageDefault;
    const context = new HttpContext().set(IS_IN_RESOLVER_CONTEXT, true);

    return combineLatest([
      documentLanguagesStorageService.primaryDocumentLanguages$,
      documentLanguagesStorageService.secondaryDocumentLanguages$,
      piaDocumentsService.getAvailableLanguages(),
      piaProductsService.getProductDisplayRangesProductIDs(
        { countryID, languageID, year, productID },
        context,
      ),
    ]).pipe(
      map(
        ([
          primaryDocumentLanguages,
          secondaryDocumentLanguages,
          availableLanguages,
          productIDs,
        ]: [
          Record<string, string[]> | null,
          Record<string, string[]> | null,
          string[] | undefined,
          string[],
        ]): [string[], string[], string] => {
          const selectedLanguage =
            webStorage.uiLanguage &&
            availableLanguages?.includes(webStorage.uiLanguage)
              ? webStorage.uiLanguage
              : (languageID ?? defaultLanguage);

          const documentLanguages = mergeDocumentLanguages(
            primaryDocumentLanguages,
            secondaryDocumentLanguages,
            countryID,
            selectedLanguage,
          );

          return [documentLanguages, productIDs, selectedLanguage];
        },
      ),
      switchMap(([includedLanguages, productIDs, selectedLanguage]) => {
        return piaDocumentsService.getUniqueDocCatsByLanguage({
          languageID: selectedLanguage,
          countryID,
          year,
          productIDs,
          includedLanguages,
        });
      }),
    );
  },
);
